import React, { useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppDispatch.js'
import { fetchCaseStudyBySlug } from '../../../../redux/slices/caseStudiesSlice.js'
import SEO from '../../../../components/seo/SEO.jsx'
import { LazyImage } from '../../../../components/common/LazyImage.jsx'
import DefaultImage from "../../../../assets/Images/Case Studies/Frame 2121458467.svg";
import ErrorBlock from '../../../../components/common/ErrorBlock.jsx'
import SkeletonSection from '../../../../components/common/SkeletonSection.jsx'
import { motion } from 'framer-motion'
import { FaCalendarAlt, FaUserTie, FaIndustry } from 'react-icons/fa'
import BuildSolution from '../../../../components/common/Common/BuildSolution/BuildSolution.jsx'

import './CaseStudyDetailPage.scss'

export default function CaseStudyDetailPage() {
  const { slug } = useParams()
  const dispatch = useAppDispatch()
  const { currentCaseStudy: caseStudy, loading, error } = useAppSelector((state) => state.caseStudies)

  useEffect(() => {
    if (slug) {
      dispatch(fetchCaseStudyBySlug(slug))
    }
  }, [dispatch, slug])

  if (loading) {
    return (
      <>
        <SEO title="Loading Case Study..." />
        <div className="space-y-12">
          <SkeletonSection />
          <SkeletonSection />
          <SkeletonSection />
        </div>
      </>
    )
  }

  if (error || !caseStudy) {
    return (
      <>
        <SEO title="Case Study Not Found" />
        <div className="container-custom section-padding">
          <ErrorBlock
            title="Case Study Not Found"
            message="The case study you're looking for doesn't exist or has been removed."
            onRetry={() => slug && dispatch(fetchCaseStudyBySlug(slug))}
          />
        </div>
      </>
    )
  }

  const hero = caseStudy.hero || {}
  const projectOverview = caseStudy.ProjectOverview || {}
  const objectives = caseStudy.Objectives || {}
  const ourSolution = caseStudy.OurSolution || {}
  const techStack = caseStudy.techStack || {}
  const impact = caseStudy.impact || {}
  const relatedConfig = caseStudy.RelatedCaseStudy || {}

  // Build detail chips for hero (using real caseStudy data)
  const detailChips = []

  if (caseStudy.client?.name) {
    detailChips.push({
      icon: <FaUserTie />,
      text: caseStudy.client.name
    })
  }

  if (caseStudy.client?.industry) {
    detailChips.push({
      icon: <FaIndustry />,
      text: caseStudy.client.industry
    })
  }

  if (caseStudy.duration?.value && caseStudy.duration?.unit) {
    detailChips.push({
      icon: <FaCalendarAlt />,
      text: `${caseStudy.duration.value} ${caseStudy.duration.unit}`
    })
  }

  if (!detailChips.length && Array.isArray(hero.spans)) {
    hero.spans.forEach((span) => {
      if (span) {
        detailChips.push({
          icon: <FaCalendarAlt />,
          text: span
        })
      }
    })
  }

  // Objectives items (for project circle layout)
  const objectiveCards = Array.isArray(objectives.cards) ? objectives.cards : []

  // Tech stack cards
  const techCards = Array.isArray(techStack.cards) ? techStack.cards : []

  // Impact / results cards
  const impactCards = Array.isArray(impact.cards) ? impact.cards : []

  // Related case studies
  const related = Array.isArray(caseStudy.relatedCaseStudies)
    ? caseStudy.relatedCaseStudies
    : []

  return (
    <>
      <SEO
        title={caseStudy.metaTitle || caseStudy.title}
        description={caseStudy.metaDescription || caseStudy.excerpt}
        image={hero.imageUrl || caseStudy.featuredImage}
        type="article"
        publishedTime={caseStudy.publishedAt}
        modifiedTime={caseStudy.updatedAt}
        author={caseStudy.client?.name}
        tags={caseStudy.tags}
      />

      <article className="case-study-detail-page">
        {/* HERO – dark v1 style, v2 data */}
        {hero.isVisible !== false && (
          <section className="case-study-hero-detail">
            <div className="case-study-container">
              <div className="case-study-grid">
                {/* Left Column - Content */}
                <motion.div
                  className="content-column"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <motion.h1
                    className="case-study-heading"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    {(hero.heading || caseStudy.title || '').toUpperCase()}
                  </motion.h1>

                  {detailChips.length > 0 && (
                    <motion.div
                      className="details-container"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                    >
                      {detailChips.map((chip, index) => (
                        <motion.div
                          key={`${chip.text}-${index}`}
                          className="detail-item"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                        >
                          <span className="detail-icon">{chip.icon}</span>
                          <span className="detail-text">{chip.text}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}

                  {hero.buttonText && (
                    <motion.a
                      href="/contact"
                      className="case-study-primary-btn"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.8 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {hero.buttonText}
                    </motion.a>
                  )}
                </motion.div>

                {/* Right Column - Image */}
                {(hero.imageUrl || caseStudy.featuredImage) && (
                  <motion.div
                    className="image-column"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                  >
                    <motion.div
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.8, delay: 0.5 }}
                      whileHover={{ scale: 1.02 }}
                      className="case-study-image-wrapper"
                    >
                      <LazyImage
                        src={hero.imageUrl || caseStudy.featuredImage}
                        alt={hero.heading || caseStudy.title}
                        className="case-study-image"
                        placeholder={
                          <div className="case-study-image-placeholder" />
                        }
                      />
                    </motion.div>
                  </motion.div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* PROJECT OVERVIEW + OBJECTIVES – v1 “ProjectAndObjective” layout */}
        {(projectOverview.isVisible !== false ||
          (objectives.isVisible !== false && objectiveCards.length > 0)) && (
            <section className="project-and-objective">
              {projectOverview.isVisible !== false && (
                <motion.div
                  className="about-section"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  {projectOverview.heading && (
                    <h2 className="section-heading">{projectOverview.heading}</h2>
                  )}
                  {(projectOverview.Para1 || projectOverview.Para2) && (
                    <p className="section-description">
                      {[projectOverview.Para1, projectOverview.Para2]
                        .filter(Boolean)
                        .join(' ')}
                    </p>
                  )}
                </motion.div>
              )}

              {objectives.isVisible !== false && objectiveCards.length > 0 && (
                <>
                  <motion.h2
                    className="project-title"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={{
                      hidden: { opacity: 0, scale: 0.9 },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        transition: { duration: 0.7, ease: 'easeOut' }
                      }
                    }}
                  >
                    {objectives.Heading || 'Project Objectives'}
                  </motion.h2>

                  <div className="image-container">
                    <motion.div
                      className="image-wrapper"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={{
                        hidden: { opacity: 0, scale: 0.95 },
                        visible: {
                          opacity: 1,
                          scale: 1,
                          transition: { duration: 0.7, ease: 'easeOut' }
                        }
                      }}
                    >
                      {/* Central image (decorative) */}
                      <LazyImage
                        src={DefaultImage}
                        alt={objectives.Heading || caseStudy.title}
                        className="project-image"
                        placeholder={<div className="project-image-placeholder" />}
                      />

                      {/* Up to 5 objective items around the image */}
                      {objectiveCards[0] && (
                        <>
                          <motion.p
                            className="image-wrapper-image-one"
                            variants={{}}
                          >
                            {objectiveCards[0].imageUrl && (
                              <LazyImage
                                src={objectiveCards[0].imageUrl}
                                alt={objectiveCards[0].title}
                                className="project-image-icon"
                                placeholder={
                                  <div className="project-image-icon-placeholder" />
                                }
                              />
                            )}
                          </motion.p>
                          <motion.p
                            className="image-wrapper-text-one image-wrapper-text"
                            variants={{}}
                          >
                            {objectiveCards[0].title}
                          </motion.p>
                        </>
                      )}

                      {objectiveCards[1] && (
                        <>
                          <motion.p
                            className="image-wrapper-image-two"
                            variants={{}}
                          >
                            {objectiveCards[1].imageUrl && (
                              <LazyImage
                                src={objectiveCards[1].imageUrl}
                                alt={objectiveCards[1].title}
                                className="project-image-icon"
                                placeholder={
                                  <div className="project-image-icon-placeholder" />
                                }
                              />
                            )}
                          </motion.p>
                          <motion.p
                            className="image-wrapper-text-two image-wrapper-text"
                            variants={{}}
                          >
                            {objectiveCards[1].title}
                          </motion.p>
                        </>
                      )}

                      {objectiveCards[2] && (
                        <>
                          <motion.p
                            className="image-wrapper-image-three"
                            variants={{}}
                          >
                            {objectiveCards[2].imageUrl && (
                              <LazyImage
                                src={objectiveCards[2].imageUrl}
                                alt={objectiveCards[2].title}
                                className="project-image-icon"
                                placeholder={
                                  <div className="project-image-icon-placeholder" />
                                }
                              />
                            )}
                          </motion.p>
                          <motion.p
                            className="image-wrapper-text-three image-wrapper-text"
                            variants={{}}
                          >
                            {objectiveCards[2].title}
                          </motion.p>
                        </>
                      )}

                      {objectiveCards[3] && (
                        <>
                          <motion.p
                            className="image-wrapper-image-four"
                            variants={{}}
                          >
                            {objectiveCards[3].imageUrl && (
                              <LazyImage
                                src={objectiveCards[3].imageUrl}
                                alt={objectiveCards[3].title}
                                className="project-image-icon"
                                placeholder={
                                  <div className="project-image-icon-placeholder" />
                                }
                              />
                            )}
                          </motion.p>
                          <motion.p
                            className="image-wrapper-text-four image-wrapper-text"
                            variants={{}}
                          >
                            {objectiveCards[3].title}
                          </motion.p>
                        </>
                      )}

                      {objectiveCards[4] && (
                        <>
                          <motion.p
                            className="image-wrapper-image-five"
                            variants={{}}
                          >
                            {objectiveCards[4].imageUrl && (
                              <LazyImage
                                src={objectiveCards[4].imageUrl}
                                alt={objectiveCards[4].title}
                                className="project-image-icon"
                                placeholder={
                                  <div className="project-image-icon-placeholder" />
                                }
                              />
                            )}
                          </motion.p>
                          <motion.p
                            className="image-wrapper-text-five image-wrapper-text"
                            variants={{}}
                          >
                            {objectiveCards[4].title}
                          </motion.p>
                        </>
                      )}
                    </motion.div>
                  </div>
                </>
              )}
            </section>
          )}

        {/* OUR SOLUTION – v1 “OurSolutions” vertical cards, v2 data */}
        {ourSolution.isVisible !== false &&
          Array.isArray(ourSolution.cards) &&
          ourSolution.cards.length > 0 && (
            <section className="vertical-cards-section">
              <div className="vertical-cards-container">
                <motion.h2
                  className="section-heading"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  {(ourSolution.Heading || 'Our Solution').toUpperCase()}
                </motion.h2>

                {ourSolution.Subheading && (
                  <motion.p
                    className="solution-subheading"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    {ourSolution.Subheading}
                  </motion.p>
                )}

                <div className="vertical-cards">
                  {ourSolution.cards.map((card, index) => (
                    <motion.div
                      key={index}
                      className="process-card"
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.2 }}
                    >
                      <div className="card-image">
                        {card.imageUrl && (
                          <LazyImage
                            src={card.imageUrl}
                            alt={card.heading}
                            placeholder={
                              <div className="card-image-placeholder" />
                            }
                          />
                        )}
                        <div className="image-overlay"></div>
                      </div>

                      <div className="card-content">
                        <h3 className="card-heading">
                          <span className="heading-number">0{index + 1}.</span>
                          {card.heading}
                        </h3>
                        {card.subheading && (
                          <p className="card-para">{card.subheading}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          )}

        <BuildSolution />

        {/* TECH STACK – v1 “TechStack” icons, v2 data */}
        {techStack.isVisible !== false && techCards.length > 0 && (
          <section className="icon-features-section">
            <div className="features-container">
              {techStack.capsuleText && (
                <motion.div
                  className="centered-capsule"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  {techStack.capsuleText}
                </motion.div>
              )}

              <motion.h2
                className="section-heading"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {(techStack.Heading || 'Our Tech Stack').toUpperCase()}
              </motion.h2>

              <motion.div
                className="features-grid"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {techCards.map((card, index) => (
                  <motion.div
                    key={index}
                    className="feature-item"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="feature-icon">
                      {card.imageUrl && (
                        <LazyImage
                          src={card.imageUrl}
                          alt={card.title}
                          className="tech-icon-image"
                          placeholder={
                            <div className="tech-icon-placeholder" />
                          }
                        />
                      )}
                    </div>
                    <span className="feature-text">{card.title}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
        )}

        {/* IMPACT / RESULTS – v1 “ResultsDelivered”, v2 data */}
        {impact.isVisible !== false && impactCards.length > 0 && (
          <section className="results-delivered-section">
            <div className="results-container">
              <motion.h2
                className="section-heading"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {(impact.Heading || "Results We've Delivered").toUpperCase()}
              </motion.h2>

              <div className="results-grid">
                {/* First row */}
                <motion.div
                  className="results-row"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {impactCards.slice(0, 2).map((card, index) => (
                    <motion.div
                      key={`impact-${index}`}
                      className="result-card"
                      whileHover={{ y: -5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="card-image-row">
                        {card.imageUrl && (
                          <LazyImage
                            src={card.imageUrl}
                            alt={card.title}
                            className="result-icon"
                            placeholder={
                              <div className="result-icon-placeholder" />
                            }
                          />
                        )}
                      </div>
                      <div className="card-content-row">
                        <p className="result-description">{card.title}</p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Second row */}
                {impactCards.length > 2 && (
                  <motion.div
                    className="results-row"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    {impactCards.slice(2, 4).map((card, index) => (
                      <motion.div
                        key={`impact-2-${index}`}
                        className="result-card"
                        whileHover={{ y: -5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="card-image-row">
                          {card.imageUrl && (
                            <LazyImage
                              src={card.imageUrl}
                              alt={card.title}
                              className="result-icon"
                              placeholder={
                                <div className="result-icon-placeholder" />
                              }
                            />
                          )}
                        </div>
                        <div className="card-content-row">
                          <p className="result-description">{card.title}</p>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* RELATED CASE STUDIES – simple dark grid */}
        {relatedConfig?.isVisible !== false && related.length > 0 && (
          <section className="related-case-studies-section">
            <div className="related-container">
              {relatedConfig.Heading && (
                <h2 className="related-heading">
                  {relatedConfig.Heading}
                </h2>
              )}

              <div className="related-grid">
                {related.slice(0, 3).map((relatedCaseStudy) => (
                  <Link
                    key={relatedCaseStudy._id}
                    to={`/case-studies/${relatedCaseStudy.slug}`}
                    className="related-card"
                  >
                    <h3 className="related-title">
                      {relatedCaseStudy.hero?.heading || relatedCaseStudy.title}
                    </h3>
                    {relatedCaseStudy.excerpt && (
                      <p className="related-excerpt">
                        {relatedCaseStudy.excerpt}
                      </p>
                    )}
                    {relatedCaseStudy.client?.industry && (
                      <span className="related-pill">
                        {relatedCaseStudy.client.industry}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </>
  )
}
